import { useEffect, useRef, useState } from 'react';
import type { Viewing } from '@bbbk/schema';
import { api, ApiError } from '../../lib/api';
import { prefetchBypass, useFetchTrigger } from '../../lib/useFetchTrigger';
import { sameWindow } from '../bookings/domain';
import type { DateWindow } from '../bookings/types';

// Viewings for a date window. Modelled line for line on useHolidays, and the two
// pieces of state that look like belt-and-braces are neither:
//
// `window` echoes the range the viewings are actually FOR. The set is HELD
// across a window change so the desk never blanks while stepping the date — and
// a consumer drawing a conclusion from a held set ("nobody is coming today")
// cannot otherwise tell it from the requested one.
//
// `failed` is scoped to the WINDOW that failed, not a bare boolean. A flag set
// on failure is only ever cleared by a later success, so one bad day made every
// day stepped to afterwards look broken before its own request had said
// anything (PR #164, claude P2).
//
// An empty window and a failed one must never look alike here. A desk that
// quietly shows no viewings because a request fell over is the same class of lie
// as a bar claiming "nothing to chase" before the look-ahead has landed.
export function useViewings(
  from: string,
  to: string,
  // Windows to cache-warm once this one has settled, so a step to a neighbour
  // resolves from the browser cache in a frame rather than a round trip. The
  // bookings look-ahead has always done this; this read did not, and since
  // the bar waits for both reads, every date step dimmed it for one full
  // round trip and read as a flicker (Sir, 2026-09-02).
  prefetch: DateWindow[] = [],
): { viewings: Viewing[]; window: DateWindow | null; failed: boolean; reload: () => void } {
  const [viewings, setViewings] = useState<Viewing[]>([]);
  const [window_, setWindow] = useState<DateWindow | null>(null);
  const [failedFor, setFailedFor] = useState<DateWindow | null>(null);
  const trigger = useFetchTrigger();

  useEffect(() => {
    const run = trigger.begin();
    api<Viewing[]>(`/viewings?from=${from}&to=${to}`, run.init)
      .then((body) => {
        if (!run.isCurrent()) return; // superseded by a later window
        run.markContent();
        setViewings(body.data ?? []);
        setWindow({ from, to });
        setFailedFor(null);
      })
      .catch((e: unknown) => {
        // api() has already redirected on a 401 — an error state here would
        // flash over the bounce.
        if (e instanceof ApiError && e.status === 401) return;
        if (!run.isCurrent()) return;
        setFailedFor({ from, to });
      });
    return run.cancel;
  }, [from, to, trigger.nonce, trigger.tick]);

  // Warm the neighbours once the shown window has landed. A write must re-warm
  // them, not only the shown one (the same lesson as useWindowedBookings, PR
  // #158): the cache is bypassed whenever the main fetch would bypass it.
  const settled = sameWindow(window_, { from, to });
  const prefetchKey = prefetch.map((w) => `${w.from}|${w.to}`).join(',');
  // What the last prefetch run saw of the trigger — so a bypass happens on the
  // run a write caused, and not on every window move for ever after (codex,
  // PR #176).
  const prefetchSeen = useRef({ nonce: trigger.nonce, tick: trigger.tick });
  useEffect(() => {
    if (!settled) return;
    const bypass = prefetchBypass(prefetchSeen.current, trigger);
    prefetchSeen.current = { nonce: trigger.nonce, tick: trigger.tick };
    for (const w of prefetch) {
      api<Viewing[]>(`/viewings?from=${w.from}&to=${w.to}`, bypass ? { cache: 'reload' } : {}).catch(() => {});
    }
    // prefetchKey captures the window set; `prefetch` identity is not stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefetchKey, settled, trigger.tick, trigger.nonce]);

  return { viewings, window: window_, failed: sameWindow(failedFor, { from, to }), reload: trigger.reload };
}
